import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Share2, Eye, Trash2, ExternalLink, Pencil } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { api } from "../api";
import { useToast } from "../hooks/use-toast";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function PagesManager() {
  const navigate = useNavigate();
  const [choose, setChoose] = useState(false);
  const { toast } = useToast();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const [services, setServices] = useState({});

  const load = async () => { setLoading(true); try { const [{ data }, s] = await Promise.all([api.pageList(), api.myServices()]); setPages(data); setServices(s.data?.services || {}); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const pick = (tier, ai) => {
    setChoose(false);
    if (tier === "minisite" && !services.minisite?.active) {
      toast({ title: "Qora MiniSite non attivo", description: "Attiva la prova gratuita di 7 giorni per creare una pagina MiniSite." });
      navigate("/checkout?plan=minisite&interval=yearly&trial=1");
      return;
    }
    setCreating({ title: "", slug: "", tier, ai });
  };

  const create = async () => {
    if (!creating.title.trim()) return;
    const slug = slugify(creating.slug || creating.title);
    try {
      const { data } = await api.pageCreate({ title: creating.title, slug, display_name: creating.title, bio: "", theme_id: "aurora", blocks: [], socials: [], status: "draft", tier: creating.tier || "link" });
      setCreating(null); navigate(`/app/pagine/${data.id}${creating.ai ? "?ai=1" : ""}`);
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail, variant: "destructive" }); }
  };

  const del = async (p) => { await api.pageDelete(p.id); toast({ title: "Pagina eliminata" }); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-2xl md:text-3xl font-extrabold">Pagine</h1><p className="text-muted-foreground mt-1">Pagine pubbliche con link, contatti e menù.</p></div>
        <Button className="btn-grad text-white font-semibold gap-2" onClick={() => setChoose(true)} data-testid="new-page"><Plus className="h-4 w-4" /> Nuova pagina</Button>
      </div>

      {loading ? <p className="text-muted-foreground">Caricamento…</p> : pages.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white p-12 text-center">
          <Share2 className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="font-semibold mt-3">Crea la tua prima pagina</p>
          <Button className="btn-grad text-white font-semibold gap-2 mt-4" onClick={() => setCreating({ title: "", slug: "" })}><Plus className="h-4 w-4" /> Nuova pagina</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.map((p) => (
            <div key={p.id} className="rounded-2xl border border-border bg-white p-5 card-shadow">
              <div className="flex items-center justify-between">
                <Badge variant={p.status === "published" ? "secondary" : "outline"} className={p.status === "published" ? "bg-emerald-50 text-emerald-600" : "text-muted-foreground"}>{p.status === "published" ? "Pubblicata" : "Bozza"}</Badge>
                <span className="text-xs text-muted-foreground">{p.views || 0} viste</span>
              </div>
              <h3 className="font-display font-bold mt-3">{p.title}</h3>
              <p className="text-xs text-muted-foreground">/p/{p.slug}</p>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={() => navigate(`/app/pagine/${p.id}`)}><Pencil className="h-3.5 w-3.5" /> Modifica</Button>
                {p.status === "published" && <Button size="sm" variant="ghost" onClick={() => window.open(`/p/${p.slug}`, "_blank")}><ExternalLink className="h-4 w-4" /></Button>}
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(p)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={choose} onOpenChange={setChoose}>
        <DialogContent className="max-w-2xl" data-testid="page-chooser">
          <DialogHeader><DialogTitle>Come vuoi iniziare?</DialogTitle><DialogDescription>Scegli il tipo di pagina: gratuita, MiniSite o guidata da Qora AI.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-3 gap-3">
            {[["link", "GRATIS", "Qora Link", "Una pagina semplice con link, social e contatti.", "Crea gratis", false, "bg-emerald-50 text-emerald-700"], ["minisite", "7 GIORNI GRATIS", "Qora MiniSite", "Una pagina professionale completa, costruita intorno alla tua attività.", "Prova MiniSite", false, "bg-[#0c2d34] text-white"], ["minisite", "QORA AI", "Fatti consigliare da Qora AI", "Rispondi a 4 domande: ti consiglio template, blocchi e CTA.", "Inizia con Qora AI", true, "bg-[#f3faf8] text-[#123C46]"]].map(([tier, badge, title, desc, cta, ai, cls], i) => (
              <button key={i} data-testid={`choose-${ai ? "ai" : tier}`} onClick={() => pick(tier, ai)} className="text-left rounded-2xl border border-border p-4 hover:border-[#0c2d34] transition-colors">
                <span className={`text-[10px] font-extrabold tracking-wider px-2 py-0.5 rounded ${cls}`}>{badge}</span>
                <p className="font-display font-extrabold mt-2">{title}</p><p className="text-xs text-muted-foreground mt-1">{desc}</p>
                <p className="text-sm font-semibold text-[#123C46] mt-3">{cta} →</p>
              </button>))}
          </div>
          <p className="text-[11px] text-muted-foreground">Qora Link: 1 pagina gratuita per attività, nessuna carta. Qora MiniSite: 9,90 €/mese o 89 €/anno, 7 giorni di prova con carta; richiede l'attivazione del servizio.</p>
        </DialogContent>
      </Dialog>

      <Dialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nuova pagina</DialogTitle><DialogDescription>Titolo e indirizzo pubblico della pagina.</DialogDescription></DialogHeader>
          {creating && (
            <div className="space-y-3">
              <div><Label className="text-sm">Titolo</Label><Input value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} className="h-10 mt-1" placeholder="Es. La mia attività" /></div>
              <div><Label className="text-sm">Indirizzo</Label><div className="flex items-center mt-1"><span className="h-10 px-2.5 grid place-items-center rounded-l-lg border border-r-0 border-border bg-muted text-xs text-muted-foreground">/p/</span><Input value={creating.slug} onChange={(e) => setCreating({ ...creating, slug: e.target.value })} className="h-10 rounded-l-none" placeholder={slugify(creating.title) || "nome"} /></div></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setCreating(null)}>Annulla</Button><Button className="btn-grad text-white font-semibold" onClick={create}>Crea e modifica</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
